"use client"

import { cn } from "@/lib/utils"
import ReactMarkdown from "react-markdown"
import remarkGfm from 'remark-gfm'
import remarkMath from 'remark-math'
import rehypeKatex from 'rehype-katex'
import rehypeRaw from 'rehype-raw'
import { EnhancedCodeBlock } from '@/components/chat/message/markdown-code-block'
import React, { memo, useEffect, useRef } from 'react'
import { DevTypeLabel } from '@/components/auth/dev-features/dev-type-label'
import katex from 'katex'

// Import KaTeX CSS for math rendering
import 'katex/dist/katex.min.css'

// Helpers for heading anchors
const getNodeText = (node: React.ReactNode): string => {
    if (node == null) return ''
    if (typeof node === 'string' || typeof node === 'number') return String(node)
    if (Array.isArray(node)) return node.map(getNodeText).join('')
    if (React.isValidElement(node)) {
        const props = node.props as { children?: React.ReactNode }
        return getNodeText(props.children)
    }
    return ''
}

const slugify = (text: string): string => {
    const result = text
        .toLowerCase()
        .trim()
        .replace(/[^a-z0-9\s-_]/g, '')
        .replace(/[\s_]+/g, '-')
        .replace(/-+/g, '-')
    return result
}

// Utility that converts the current browser selection into a plain-text string
// while preserving list markers ("- " for unordered lists and "1. " style for
// ordered lists). This prevents losing bullet formatting when users copy the
// rendered markdown.
const selectionToPlainText = (): string | null => {
    const selection = typeof window !== 'undefined' ? window.getSelection() : null
    if (!selection || selection.rangeCount === 0) return null

    const range = selection.getRangeAt(0)
    const fragment = range.cloneContents()
    const lines: string[] = []

    const processNode = (node: Node, indent = '') => {
        if (node.nodeType === Node.ELEMENT_NODE) {
            const el = node as HTMLElement

            if (el.tagName === 'LI') {
                const parent = el.parentElement
                let marker = '- '
                if (parent && parent.tagName === 'OL') {
                    const index = Array.from(parent.children).indexOf(el) + 1
                    marker = `${index}. `
                }
                const content = el.textContent?.trim() || ''
                lines.push(`${indent}${marker}${content}`)
                return
            }

            if (el.tagName === 'UL' || el.tagName === 'OL') {
                const newIndent = indent
                el.childNodes.forEach(child => processNode(child, newIndent))
                return
            }
        }

        if (node.nodeType === Node.TEXT_NODE) {
            const text = node.textContent?.trim()
            if (text) lines.push(`${indent}${text}`)
        } else {
            node.childNodes.forEach(child => processNode(child, indent))
        }
    }

    fragment.childNodes.forEach(node => processNode(node))
    return lines.join('\n')
}

// Helper: run a transformer only outside fenced and inline code blocks
const transformOutsideCode = (input: string, transformer: (segment: string) => string): string => {
    // Split on fenced code blocks first and keep the delimiters
    const fencedSplit = input.split(/(```[\s\S]*?```)/g)
    const processInline = (segment: string) => {
        const inlineSplit = segment.split(/(`[^`]*`)/g)
        return inlineSplit
            .map((part, idx) => (idx % 2 === 1 ? part : transformer(part)))
            .join('')
    }
    return fencedSplit
        .map((part, idx) => (idx % 2 === 1 ? part : processInline(part)))
        .join('')
}

// Convert display math blocks (multi-line $$...$$ or \[...\] or bare \begin...\end...) into fenced ```latex code blocks
export const convertDisplayMathToLatexCodeBlocks = (input: string): string => {
    const wrapLatex = (content: string) => `\n\n${'```latex'}\n${content.trim()}\n${'```'}\n\n`

    // 1) Normalize \[ ... \] to $$ ... $$ outside code
    let out = transformOutsideCode(input, (s) => s.replace(/\\\[([\s\S]*?)\\\]/g, (_m, inner) => `$$${inner}$$`))

    // 2) Convert $$...$$ blocks (entire content preserved)
    out = transformOutsideCode(out, (s) => s.replace(/\$\$([\s\S]*?)\$\$/g, (_m, inner) => wrapLatex(inner)))

    // 3) Convert bare \begin...\end... sequences on their own lines
    out = transformOutsideCode(out, (s) => s.replace(/(^|\n)\s*(\\begin\{[\s\S]*?\\end\{[\s\S]*?\})\s*(?=\n|$)/g, (_m, prefix: string, body: string) => `${prefix}${wrapLatex(body)}`))

    return out
}

export const processMarkdownContent = (content: string, citations: Array<{title?: string, url: string, date?: string}> = []): string => {
    // Replace em dashes with semicolons
    let processed = content.replace(/—/g, '; ')

    // First, convert display math into fenced latex code blocks for pretty rendering
    processed = convertDisplayMathToLatexCodeBlocks(processed)

    // Then, handle inline math in parentheses. If it spans multiple lines, convert to code block; otherwise keep inline
    processed = transformOutsideCode(processed, (segment) => {
        // Multiline inline parentheses -> code block
        segment = segment.replace(/\\\(([\s\S]*?)\\\)/g, (_m, inner: string) => {
            const hasNewline = /\n/.test(inner)
            if (hasNewline) {
                const content = inner.trim()
                return `\n\n${'```latex'}\n${content}\n${'```'}\n\n`
            }
            return `$${inner}$`
        })

        // Multiline single-dollar math -> code block (conservative; only if newline inside)
        segment = segment.replace(/\$([\s\S]*?)\$/g, (_m, inner: string) => {
            if (/\n/.test(inner)) {
                const content = inner.trim()
                return `\n\n${'```latex'}\n${content}\n${'```'}\n\n`
            }
            return `$${inner}$`
        })

        return segment
    })

    // Process citation patterns like [1], [2], etc.
    processed = processed.replace(/\[(\d+)\]/g, (match, num) => {
        const citationIndex = parseInt(num) - 1
        if (citations[citationIndex]) {
            return `[${num}](citation-${num})`
        }
        return match
    })

    // Convert ==highlight== to <mark>highlight</mark>
    processed = processed.replace(/==([^=\n][^=]*?)==/g, (_match, inner) => `<mark class="bg-primary/80 text-foreground px-1">${inner}</mark>`)

    return processed
}

const NonMemoizedEnhancedMarkdown = ({ 
    children, 
    compact = false,
    citations = [],
    disableProse = false,
}: { 
    children: string,
    compact?: boolean,
    citations?: Array<{
        title?: string
        url: string
        date?: string
    }>,
    disableProse?: boolean,
}) => {
    const markdownRef = useRef<HTMLDivElement>(null)

    // Add copy event listener to handle plain text copying
    useEffect(() => {
        const handleCopy = (e: ClipboardEvent) => {
            if (window.getSelection) {
                const selection = window.getSelection()
                if (selection?.toString()) {
                    const plainText = selectionToPlainText()
                    if (plainText) {
                        e.preventDefault()
                        e.clipboardData?.setData('text/plain', plainText)
                    }
                }
            }
        }

        const element = markdownRef.current
        element?.addEventListener('copy', handleCopy as EventListener)
        
        return () => {
            element?.removeEventListener('copy', handleCopy as EventListener)
        }
    }, [])

    const processedChildren = processMarkdownContent(children, citations)

    return (
        <div 
            className={cn("w-full max-w-full markdown-content", disableProse ? "contents" : undefined)}
            ref={markdownRef}
            style={{
                '--katex-display-overflow': 'auto'
            } as React.CSSProperties}
        >
            <div className={cn(
                disableProse ? "contents" : "prose dark:prose-invert max-w-none [&>*:first-child]:mt-0 [&>*:last-child]:mb-0",
                // KaTeX scrolling helpers should apply regardless of prose wrapper
                "[&_.katex-display]:overflow-x-auto [&_.katex-display]:overflow-y-hidden [&_.katex-display]:max-w-full [&_.katex-display]:py-2",
                "[&_.katex-display_.katex]:whitespace-nowrap [&_.katex-display_.katex]:min-w-fit",
            )}>
                <ReactMarkdown
                    remarkPlugins={[remarkGfm, remarkMath]}
                    rehypePlugins={[rehypeRaw, rehypeKatex]}
                    components={{
                        // @ts-expect-error - Type issues with ReactMarkdown components
                        code: ({ node, inline, className, children, ...props }) => {
                            if (!inline) {
                                // Leave block code to <pre> handler
                                return <code className={className} {...props}>{children}</code>
                            }
                            let content = String(children).replace(/\n$/, '')
                            // Convert <br/> tags to line breaks in inline code
                            content = content.replace(/<br\s*\/>/gi, '\n')

                            return (
                                <span className="relative inline-block">
                                    <DevTypeLabel type="code" />
                                    <code
                                        className={cn(
                                            "relative rounded bg-muted font-mono font-medium whitespace-pre-wrap leading-6",
                                            compact ? "px-[0.25rem] py-[0.15rem] text-xs" : "px-[0.3rem] py-[0.2rem] text-xs"
                                        )}
                                    >
                                        {content}
                                    </code>
                                </span>
                            )
                        },
                        pre: ({ node, children, ...props }) => {
                            const codeElement = React.isValidElement(children)
                                ? children 
                                : (Array.isArray(children) && children.length > 0 
                                    ? children[0] 
                                    : null)
                            
                            const language = codeElement?.props?.className?.match(/language-(\w+)/)?.[1] || ''
                            let codeContent = codeElement?.props?.children || ''
                            
                            // Convert <br/> tags to line breaks in code blocks
                            if (typeof codeContent === 'string') {
                                codeContent = codeContent.replace(/<br\s*\/>/gi, '\n')
                            }
                            
                            // Handle LaTeX code blocks specially
                            if (language === 'latex' || language === 'tex') {
                                try {
                                    const raw = String(codeContent)
                                    // If this is a full LaTeX document, skip KaTeX render and just show code
                                    if (/\\documentclass|\\begin\{document\}|\\usepackage/.test(raw)) {
                                        throw new Error('skip-katex-full-document')
                                    }
                                    const renderedMath = katex.renderToString(raw, {
                                        displayMode: true,
                                        throwOnError: false,
                                        trust: true,
                                        strict: false
                                    })
                                    
                                    return (
                                        <div className="relative">
                                            <DevTypeLabel type="pre(latex)" />
                                            <div className={cn(
                                                "my-4 p-4 bg-muted/30 rounded-lg border",
                                                compact ? "my-2 p-2" : "my-4 p-4"
                                            )}>
                                                <div className="overflow-x-auto">
                                                    <div 
                                                        className="katex-display-block text-center min-w-fit"
                                                        dangerouslySetInnerHTML={{ __html: renderedMath }}
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    )
                                } catch (error) {
                                    // Fallback to regular code block if LaTeX parsing fails or is a full document
                                    const rawText = String(codeContent)
                                    const equations: string[] = []

                                    // $$ ... $$ blocks
                                    try {
                                        const dollarRe = /\$\$([\s\S]*?)\$\$/g
                                        for (const m of rawText.matchAll(dollarRe)) {
                                            equations.push((m[1] || '').trim())
                                        }
                                    } catch {}

                                    // \[ ... \] blocks
                                    try {
                                        const bracketRe = /\\\[([\s\S]*?)\\\]/g
                                        for (const m of rawText.matchAll(bracketRe)) {
                                            equations.push((m[1] || '').trim())
                                        }
                                    } catch {}

                                    // \begin{equation} ... \end{equation}
                                    try {
                                        const eqnRe = /\\begin\{equation\}([\s\S]*?)\\end\{equation\}/g
                                        for (const m of rawText.matchAll(eqnRe)) {
                                            equations.push((m[1] || '').trim())
                                        }
                                    } catch {}

                                    // \begin{align} ... \end{align} (and align*) -> wrap with aligned
                                    try {
                                        const alignRe = /\\begin\{align\*?\}([\s\S]*?)\\end\{align\*?\}/g
                                        for (const m of rawText.matchAll(alignRe)) {
                                            const inner = (m[1] || '').trim()
                                            equations.push(`\\begin{aligned}\n${inner}\n\\end{aligned}`)
                                        }
                                    } catch {}

                                    let previews: React.ReactNode = null
                                    if (equations.length > 0) {
                                        const blocks = equations.map((expr, idx) => {
                                            try {
                                                const html = katex.renderToString(expr, {
                                                    displayMode: true,
                                                    throwOnError: false,
                                                    trust: true,
                                                    strict: false
                                                })
                                                return (
                                                    <div
                                                        key={idx}
                                                        className="katex-display-block text-center min-w-fit"
                                                        dangerouslySetInnerHTML={{ __html: html }}
                                                    />
                                                )
                                            } catch {
                                                return null
                                            }
                                        })
                                        previews = (
                                            <div className="overflow-x-auto mb-3 flex flex-col items-center gap-2">
                                                {blocks}
                                            </div>
                                        )
                                    }

                                    // If we have previews, show only those; otherwise show the raw code block
                                    if (previews) {
                                        return (
                                            <div className="relative">
                                                <DevTypeLabel type="pre(latex-preview)" />
                                                <div className={cn(
                                                    "my-4 p-4 bg-muted/30 rounded-lg border",
                                                    compact ? "my-2 p-2" : "my-4 p-4"
                                                )}>
                                                    {previews}
                                                </div>
                                            </div>
                                        )
                                    }

                                    return (
                                        <div className="relative">
                                            <DevTypeLabel type="pre(latex-code)" />
                                            <div className={cn(
                                                "my-4 p-4 bg-muted/30 rounded-lg border",
                                                compact ? "my-2 p-2" : "my-4 p-4"
                                            )}>
                                                <div className="text-sm mb-2 text-muted-foreground">LaTeX Code</div>
                                                <EnhancedCodeBlock
                                                    inline={false}
                                                    className={cn("leading-6", codeElement?.props?.className)}
                                                    compact={compact}
                                                    language="latex"
                                                >
                                                    {codeContent}
                                                </EnhancedCodeBlock>
                                            </div>
                                        </div>
                                    )
                                }
                            }
                            
                            return (
                                <div className="relative">
                                    <DevTypeLabel type="pre" />
                                    <EnhancedCodeBlock
                                        inline={false}
                                        className={cn("leading-6", codeElement?.props?.className)}
                                        compact={compact}
                                        language={language}
                                        theme="github-dark"
                                    >
                                        {codeContent}
                                    </EnhancedCodeBlock>
                                </div>

                            )
                        },
                        // List styling that maintains selection
                        ol: ({ children }) => (
                            <div className="relative">
                                <DevTypeLabel type="ol" />
                                <ol className={cn(
                                    "list-decimal list-outside", 
                                    compact ? "text-base my-1 ml-4 leading-7" : "my-4 ml-6 text-base leading-7"
                                )}>
                                    {children}
                                </ol>
                            </div>
                        ),
                        ul: ({ children }) => (
                            <div className="relative">
                                <DevTypeLabel type="ul" />
                                <ul className={cn(
                                    "list-disc list-outside", 
                                    compact ? "text-base ml-4 leading-7" : "my-4 ml-6 text-base leading-7"
                                )}>
                                    {children}
                                </ul>
                            </div>
                        ),
                        li: ({ children }) => (
                            <li className={cn(
                                "relative",
                                compact ? "text-base mt-0.5 py-0 leading-6" : "mt-1 text-base leading-7"
                            )}>
                                <DevTypeLabel type="li" />
                                {children}
                            </li>
                        ),
                        // Typography
                        p: ({ children }) => {
                            const hasBlockLevelChild = React.Children.toArray(children).some(child => {
                                if (!React.isValidElement(child)) return false
                                const t = child.type as unknown
                                const tag = typeof t === 'string' ? t : null
                                if (!tag) return false
                                return [
                                    'div','table','pre','blockquote','ul','ol','hr',
                                    'h1','h2','h3','h4','h5','h6'
                                ].includes(tag)
                            })

                            const Tag = hasBlockLevelChild ? 'div' as const : 'p' as const

                            return (
                                <Tag className={cn(
                                    "relative",
                                    "text-base leading-7"
                                )}>
                                    <DevTypeLabel type="p" />
                                    {children}
                                </Tag>
                            )
                        },
                        h1: ({ children }) => {
                            const id = slugify(getNodeText(children))
                            return (
                                <h1 id={id} data-anchor={id} className={cn(
                                    "font-bold tracking-tight relative leading-tight",
                                    compact ? "text-base mt-2 mb-1" : "scroll-m-20 text-2xl mt-8 mb-0"
                                )}>
                                    <DevTypeLabel type="h1" />
                                    {children}
                                </h1>
                            )
                        },
                        h2: ({ children }) => {
                            const id = slugify(getNodeText(children))
                            return (
                                <h2 id={id} data-anchor={id} className={cn(
                                    "font-semibold tracking-tight relative leading-tight",
                                    compact ? "text-sm mt-1.5 mb-0.5" : "scroll-m-20 text-xl mt-6 mb-0"
                                )}>
                                    <DevTypeLabel type="h2" />
                                    {children}
                                </h2>
                            )
                        },
                        h3: ({ children }) => {
                            const id = slugify(getNodeText(children))
                            return (
                                <h3 id={id} data-anchor={id} className={cn(
                                    "font-semibold tracking-tight relative leading-snug",
                                    compact ? "text-sm mt-1.5 mb-0.5" : "scroll-m-20 text-lg mt-5 mb-0"
                                )}>
                                    <DevTypeLabel type="h3" />
                                    {children}
                                </h3>
                            )
                        },
                        // Other elements
                        blockquote: ({ children }) => (
                            <blockquote className={cn(
                                "border-l-2 border-border italic relative leading-7",
                                compact ? "text-xs mt-1 pl-2 mb-1" : "mt-4 pl-6 text-sm"
                            )}>
                                <DevTypeLabel type="blockquote" />
                                {children}
                            </blockquote>
                        ),
                        strong: ({ children }) => (
                            <span className="font-semibold relative inline-block"> 
                                <DevTypeLabel type="strong" />
                                {children}
                            </span>
                        ),
                        a: ({ href, children }) => {
                            // Handle citation links specially
                            if (href?.startsWith('citation-')) {
                                const citationNum = href.replace('citation-', '')
                                const citationIndex = parseInt(citationNum) - 1
                                const citation = citations[citationIndex]
                                
                                if (citation) {
                                    return (
                                        <span className="relative inline-block">
                                            <DevTypeLabel type="a[citation]" />
                                            <a 
                                                href={citation.url} 
                                                target="_blank" 
                                                rel="noopener noreferrer" 
                                                className="inline-flex items-center justify-center w-3.5 h-3.5 bg-muted hover:bg-muted/80 text-primary hover:text-primary/80 no-underline text-[9px] font-medium cursor-pointer rounded-sm ml-0.5"
                                                title={citation.title || citation.url}
                                            >
                                                {children}
                                            </a>
                                        </span>
                                    )
                                }
                            }
                            
                            // Regular links: make SSR deterministic without relying on window
                            const isHashOnly = href?.startsWith('#')
                            const isExternalHttp = !!href && /^(https?:)?\/\//i.test(href)
                            const shouldOpenNewTab = !isHashOnly && isExternalHttp

                            let normalizedHash = ''
                            if (isHashOnly) {
                                normalizedHash = href?.slice(1) || ''
                            }

                            const handleClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
                                if (isHashOnly) {
                                    const hash = normalizedHash
                                    if (hash) {
                                        const el = document.getElementById(hash) || document.querySelector(`[data-anchor='${hash}']`)
                                        if (el) {
                                            e.preventDefault()
                                            ;(el as HTMLElement).scrollIntoView({ behavior: 'smooth', block: 'start' })
                                        }
                                    }
                                }
                            }

                            return (
                                <span className="relative inline-block">
                                    <DevTypeLabel type="a" />
                                    <a 
                                        href={href}
                                        onClick={handleClick}
                                        target={shouldOpenNewTab ? "_blank" : undefined}
                                        rel={shouldOpenNewTab ? "noopener noreferrer" : undefined}
                                        className="font-medium text-primary underline underline-offset-4 hover:text-primary/80" 
                                    >
                                        {children}
                                    </a>
                                </span>
                            )
                        },
                        // Table components
                        table: ({ children }) => (
                            <div className={cn(
                                "w-full overflow-y-auto relative",
                                compact ? "my-1.5" : "my-4"
                            )}>
                                <DevTypeLabel type="table" />
                                <table className="w-full border-collapse border border-border">{children}</table>
                            </div>
                        ),
                        thead: ({ children }) => <thead className="bg-muted/30">{children}</thead>,
                        tbody: ({ children }) => <tbody>{children}</tbody>,
                        tr: ({ children }) => <tr className="border-b border-border">{children}</tr>,
                        td: ({ children }) => {
                            // If a td directly contains block-level children, wrap those in a div to keep DOM valid
                            const content = React.Children.map(children as React.ReactNode, child => {
                                if (!React.isValidElement(child)) return child
                                const t = child.type as unknown
                                const tag = typeof t === 'string' ? t : null
                                if (tag && ['div'].includes(tag)) return child
                                if (tag && ['p','ul','ol','pre','table','blockquote','hr','h1','h2','h3','h4','h5','h6'].includes(tag)) {
                                    return <div className="contents">{child}</div>
                                }
                                return child
                            })
                            return (
                                <td className={cn(
                                    "border border-border relative leading-6",
                                    compact ? "p-0.5 text-xs" : "p-1.5 text-sm"
                                )}>
                                    <DevTypeLabel type="td" />
                                    {content}
                                </td>
                            )
                        },
                        th: ({ children }) => (
                            <th className={cn(
                                "border border-border font-semibold text-left relative leading-6",
                                compact ? "p-0.5 text-xs" : "p-1.5 text-sm"
                            )}>
                                <DevTypeLabel type="th" />
                                {children}
                            </th>
                        ),
                        hr: () => (
                            <div className="relative">
                                <DevTypeLabel type="hr" />
                                <hr className={cn(
                                    "border-border",
                                    compact ? "my-1.5" : "my-4"
                                )} />
                            </div>
                        ),
                        img: ({ src, alt }) => (
                            compact ? (
                                <span className="relative inline-block text-xs text-muted-foreground my-1 italic">
                                    <DevTypeLabel type="img[compact]" />
                                    [Image: {alt || "image"}]
                                </span>
                            ) : (
                                <span className="relative flex justify-center my-4">
                                    <DevTypeLabel type="img" />
                                    <img 
                                        src={src} 
                                        alt={alt} 
                                        className="rounded-lg border border-border max-w-full h-auto" 
                                    />
                                </span>
                            )
                        ),
                        br: () => (
                            <span className="relative inline-block">
                                <DevTypeLabel type="br" />
                                <br className="block" />
                            </span>
                        ),
                    }}
                >
                    {processedChildren}
                </ReactMarkdown>
            </div>
        </div>
    )
}




// Export both memoized and non-memoized versions
export { NonMemoizedEnhancedMarkdown }

export const EnhancedMarkdown = memo(
    NonMemoizedEnhancedMarkdown,
    (prevProps, nextProps) => 
        prevProps.children === nextProps.children && 
        prevProps.compact === nextProps.compact &&
        JSON.stringify(prevProps.citations) === JSON.stringify(nextProps.citations)
) 

